/**
 * #330 — where a camera looks: the geometry of its facing wedge on the fleet map, and the rules for the three
 * values a superuser sets (heading 0–359°, width 10–180°, range 5–500 m; the API holds the same ranges).
 * Pure: no React, no Google. Angles are compass bearings in degrees, clockwise from north.
 */

export interface LatLng { lat: number; lng: number }

/** Below this zoom the wedges are hidden (owner 2026-10-13: "hidden until zoomed in"). */
export const WEDGE_MIN_ZOOM = 15
/** #339 — the width and the range a camera with a heading but no width or range of its own is drawn with are a map
 *  setting (lib/mapSettings wedgeDefaults; client 2026-10-14: 40 degrees, 10 m). These two are only its fallback,
 *  DEFAULT_MAP_SETTINGS, for an API that does not send it; what draws reads the settings. */
export const DEFAULT_FOV = 40
export const DEFAULT_RANGE_M = 10
/** #330 round 2 (owner 2026-10-13) — the shortest a stored wedge is drawn on screen, in pixels: at zoom 15-16 a
 *  30 m wedge was 10-20 px long and sat under the pin. Visual only; the stored range is never changed. Fix round
 *  (I-3), #339: in the confirm box or the sheet a TYPED range is drawn at its true size, so a range edit shows at
 *  any zoom; an empty range (following the default) keeps the minimum, as does the wedge that follows the mouse
 *  while picking (fix round 2). */
export const MIN_WEDGE_PX = 40
/** Web Mercator ground resolution at zoom 0 on the equator, in metres per pixel (256 px tiles). */
const METRES_PER_PX_Z0 = 156543.03392

export const HEADING_MIN = 0
export const HEADING_MAX = 359
export const FOV_MIN = 10
export const FOV_MAX = 180
export const RANGE_MIN_M = 5
export const RANGE_MAX_M = 500

/** The mean Earth radius (IUGG), in metres. */
const EARTH_RADIUS_M = 6371008.8
/** One arc point every this many degrees (fewer for a width that is not a multiple of it). */
const ARC_STEP_DEG = 5

/** #339 — the width and the range drawn for a camera that has none of its own (the map settings', wedgeDefaults). */
export interface WedgeDefaults { fov: number; rangeM: number }

/** The 8 compass presets of the editors, N first, clockwise. */
export const COMPASS: ReadonlyArray<{ label: string; deg: number }> = [
  { label: 'N', deg: 0 }, { label: 'NE', deg: 45 }, { label: 'E', deg: 90 }, { label: 'SE', deg: 135 },
  { label: 'S', deg: 180 }, { label: 'SW', deg: 225 }, { label: 'W', deg: 270 }, { label: 'NW', deg: 315 },
]

const RAD = Math.PI / 180

/** Any angle in degrees, brought into 0 (inclusive) .. 360 (exclusive). */
export function normaliseHeading(n: number): number {
  return ((n % 360) + 360) % 360
}

/** The point `distanceM` metres from `from` along the initial bearing `bearingDeg` (on a sphere). */
function destination(from: LatLng, bearingDeg: number, distanceM: number): LatLng {
  const delta = distanceM / EARTH_RADIUS_M
  const theta = bearingDeg * RAD
  const phi1 = from.lat * RAD
  const lambda1 = from.lng * RAD
  const phi2 = Math.asin(Math.sin(phi1) * Math.cos(delta) + Math.cos(phi1) * Math.sin(delta) * Math.cos(theta))
  const lambda2 = lambda1 + Math.atan2(
    Math.sin(theta) * Math.sin(delta) * Math.cos(phi1),
    Math.cos(delta) - Math.sin(phi1) * Math.sin(phi2),
  )
  // longitude back into -180..180
  return { lat: phi2 / RAD, lng: ((lambda2 / RAD + 540) % 360) - 180 }
}

/**
 * The wedge as a closed polygon path: the apex (the camera) first, then the arc from heading − width/2 to
 * heading + width/2, both ends included, one point every 5° (evenly spaced, at most 5° apart, when the width
 * is not a multiple of 5). #339 — the caller resolves a camera's null width or range from the map settings.
 */
export function wedgePath(center: LatLng, headingDeg: number, fov: number, range: number): LatLng[] {
  const steps = Math.max(1, Math.ceil(fov / ARC_STEP_DEG))
  const start = headingDeg - fov / 2
  const path: LatLng[] = [{ lat: center.lat, lng: center.lng }]
  for (let i = 0; i <= steps; i++) {
    path.push(destination(center, normaliseHeading(start + (fov * i) / steps), range))
  }
  return path
}

/** #342 — the tick of a raised wedge (lib/wedgeStyle): from the apex `lengthM` metres along the heading. */
export function tickPath(apex: LatLng, headingDeg: number, lengthM: number): LatLng[] {
  return [{ lat: apex.lat, lng: apex.lng }, destination(apex, headingDeg, lengthM)]
}

/** The ground one screen pixel covers at this latitude and zoom, in metres: 156543.03392 * cos(lat) / 2^zoom. #342 —
 *  also turns the tick's length on screen into metres. */
export function metresPerPx(lat: number, zoom: number): number {
  return (METRES_PER_PX_Z0 * Math.cos(lat * RAD)) / 2 ** zoom
}

/** #330 round 2 — the range a wedge is drawn with at this latitude and zoom: the stored range, or MIN_WEDGE_PX
 *  worth of metres when that is longer. */
export function drawnRange(rangeM: number, lat: number, zoom: number): number {
  return Math.max(rangeM, MIN_WEDGE_PX * metresPerPx(lat, zoom))
}

/** #330 fix round 2 — the wedge's length on the editors' compass, as a share of the compass radius. Not linear: a
 *  square root with a 0.2 floor, so a short range shows (30 m 0.40, 10 m 0.31; a linear 0-500 m scale made 30 m
 *  0.06) and 500 m reaches the rim (5 m 0.28, 100 m 0.56). Always longer for a longer range; the printed number is
 *  the truth. */
export function compassShare(rangeM: number): number {
  return 0.2 + 0.8 * Math.sqrt(rangeM / RANGE_MAX_M)
}

const round2 = (n: number) => Math.round(n * 100) / 100

/** #330 fix round (I-2) — a wedge as an SVG path for the editors' compass: the apex at (cx, cy), radius r, the arc
 *  clockwise from heading - width/2 to heading + width/2. Screen y grows downwards, so north is up. */
export function sectorPath(cx: number, cy: number, r: number, headingDeg: number, fovDeg: number): string {
  const at = (bearing: number) => {
    const a = bearing * RAD
    return `${round2(cx + r * Math.sin(a))} ${round2(cy - r * Math.cos(a))}`
  }
  const large = fovDeg > 180 ? 1 : 0
  return `M ${round2(cx)} ${round2(cy)} L ${at(headingDeg - fovDeg / 2)} A ${round2(r)} ${round2(r)} 0 ${large} 1 ${at(headingDeg + fovDeg / 2)} Z`
}

/** The initial bearing from `a` to `b`, 0..360 (north 0, east 90). */
export function bearingBetween(a: LatLng, b: LatLng): number {
  const phi1 = a.lat * RAD
  const phi2 = b.lat * RAD
  const dLambda = (b.lng - a.lng) * RAD
  const y = Math.sin(dLambda) * Math.cos(phi2)
  const x = Math.cos(phi1) * Math.sin(phi2) - Math.sin(phi1) * Math.cos(phi2) * Math.cos(dLambda)
  return normaliseHeading(Math.atan2(y, x) / RAD)
}

/** The three fields of an editor, as typed. */
export interface DirectionForm { heading: string; fov: string; range: string }

/** The three values as the API takes them; null clears one. */
export interface DirectionValues {
  heading_degrees: number | null
  fov_degrees: number | null
  view_range_m: number | null
}

/** A camera's stored values as the editors' text fields; null shows as empty. */
export function directionForm(v: { heading?: number | null; fov?: number | null; range?: number | null }): DirectionForm {
  const text = (n: number | null | undefined) => (typeof n === 'number' ? String(n) : '')
  return { heading: text(v.heading), fov: text(v.fov), range: text(v.range) }
}

/** Text as a whole number in [min, max], or undefined when it is not one (empty included). #339 — also the map
 *  settings dialog's check of its two wedge defaults. */
export function parseWhole(raw: string, min: number, max: number): number | undefined {
  const s = raw.trim()
  if (!/^\d+$/.test(s)) return undefined
  const n = Number(s)
  return n >= min && n <= max ? n : undefined
}

/** One field: a whole number in [min, max]; empty is null unless `required`. Undefined = not valid. */
function parseField(raw: string, min: number, max: number, required = false): number | null | undefined {
  if (raw.trim() === '') return required ? undefined : null
  return parseWhole(raw, min, max)
}

/** #339 — the fields that must hold a number; any other empty field is null. */
export type RequiredFields = Partial<Record<keyof DirectionForm, boolean>>

/**
 * The editors' three fields as the API's values, or null when any of them is not valid (heading 0–359, width
 * 10–180, range 5–500 m, whole numbers). An empty field is null: a cleared heading, or (#339) a width or range that
 * follows the map default; unless it is in `required` (the map's confirm box requires the heading).
 */
export function parseDirection(form: DirectionForm, required: RequiredFields = {}): DirectionValues | null {
  const heading = parseField(form.heading, HEADING_MIN, HEADING_MAX, required.heading)
  const fov = parseField(form.fov, FOV_MIN, FOV_MAX, required.fov)
  const range = parseField(form.range, RANGE_MIN_M, RANGE_MAX_M, required.range)
  if (heading === undefined || fov === undefined || range === undefined) return null
  return { heading_degrees: heading, fov_degrees: fov, view_range_m: range }
}
